import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { Tooltip } from '@/components/ui/tooltip'

/**
 * One fact about a record — its address, its date, how many spaces — in a slot
 * that is there whether or not the record has a value for it.
 */
export interface Fact {
  /**
   * The fact's mark. Without one the label is written out instead ("Price
   * $12 / ea"), for metadata that has no obvious icon.
   */
  icon?: ReactNode
  /** What the fact is: read to a screen reader beside an icon, shown without one. */
  label: string
  /** The value; null, undefined or '' when the record has none. */
  value: ReactNode | null | undefined
  /**
   * What the slot says when there is no value — "No address yet", "Date not
   * set". Required, so a slot can never quietly collapse.
   */
  empty: string
}

/**
 * A record's facts, each in a fixed slot: the same facts in the same order for
 * every record, and a missing one saying so rather than disappearing.
 *
 * **Facts keep their slots.** On a grid of venue cards, one venue without an
 * address used to lift its space count into the line where every other card
 * has its address, so the eye found a count where it expected a street and the
 * cards stopped lining up. A slot that says "No address yet" keeps the grid
 * readable and says what is missing, which is also the useful thing to know.
 * `cse/fixed-facts` rejects a facts list built conditionally, so a fact cannot
 * be dropped by a ternary. See `design-system/COMPONENTS.md`.
 *
 * `inline` wraps the facts along one line (a record header); `stack` puts one
 * per line (a card), so the Nth fact is always on the Nth line.
 */
export function Facts({
  facts,
  layout = 'inline',
  className,
}: {
  facts: readonly Fact[]
  layout?: 'inline' | 'stack'
  className?: string
}) {
  return (
    <dl
      className={cn(
        'm-0 tabular-nums text-ink-muted',
        layout === 'inline' ? 'flex flex-wrap gap-x-5 gap-y-1.5' : 'grid gap-1',
        className,
      )}
    >
      {facts.map((fact) => {
        const missing = fact.value === null || fact.value === undefined || fact.value === ''
        // A stacked slot clips to its one line so the cards keep lining up; hand
        // the full text back on hover. An inline slot wraps instead and needs neither.
        const clipped = layout === 'stack'
        const fullText = clipped && !missing && typeof fact.value === 'string' ? fact.value : undefined
        return (
          <div key={fact.label} className="flex min-w-0 items-center gap-1.5">
            {fact.icon === undefined ? (
              <dt className="shrink-0 font-medium text-ink">{fact.label}</dt>
            ) : (
              <dt className="flex shrink-0 text-ink-subtle [&>svg]:h-[1em] [&>svg]:w-[1em]">
                {fact.icon}
                <span className="sr-only">{fact.label}</span>
              </dt>
            )}
            <Tooltip content={fullText}>
              <dd className={cn('m-0 min-w-0', clipped && 'truncate', missing && 'text-ink-subtle')}>
                {missing ? fact.empty : fact.value}
              </dd>
            </Tooltip>
          </div>
        )
      })}
    </dl>
  )
}
